import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonButton,
  IonSpinner,
  IonAlert,
  IonIcon,
} from '@ionic/react';
import { tvOutline } from 'ionicons/icons';
import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { adService } from '../services/AdService';
import { useWallet } from '../contexts/WalletContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import './WatchAd.css';

const WatchAd: React.FC = () => {
  const { earnCoins } = useWallet();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    // Initialize AdMob on mount (only on native)
    if (isNative) {
      adService.initialize().catch((error) => {
        console.error('Failed to initialize AdMob:', error);
        setErrorMessage('Failed to initialize ads. Please try again later.');
      });
    }
  }, [isNative]);

  const handleWatchRewardedAd = async () => {
    if (!isNative) {
      setErrorMessage('Rewarded ads are only available on mobile apps. Please use the app version.');
      setShowError(true);
      return;
    }

    if (!adService.isAvailable()) {
      setErrorMessage('Ads are not available at the moment. Please try again later.');
      setShowError(true);
      return;
    }

    setLoading(true);
    setShowSuccess(false);
    setShowError(false);

    try {
      const coins = await adService.showRewardedAd();
      
      if (coins > 0) {
        // Award coins to user
        await earnCoins(coins, `Watched Rewarded Ad - Earned ${coins} coins`, 'rewarded-ad');
        setCoinsEarned(coins);
        setShowSuccess(true);
      } else {
        setErrorMessage('Ad was not completed. Please watch the full ad to earn coins.');
        setShowError(true);
      }
    } catch (error) {
      console.error('Error showing rewarded ad:', error);
      setErrorMessage('Failed to show ad. Please try again.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleWatchInterstitialAd = async () => {
    if (!isNative) {
      setErrorMessage('Interstitial ads are only available on mobile apps. Please use the app version.');
      setShowError(true);
      return;
    }

    if (!adService.isAvailable()) {
      setErrorMessage('Ads are not available at the moment. Please try again later.');
      setShowError(true);
      return;
    }

    setLoading(true);
    setShowSuccess(false);
    setShowError(false);

    try {
      const coins = await adService.showInterstitialAd();
      
      if (coins > 0) {
        // Award coins to user
        await earnCoins(coins, `Watched Interstitial Ad - Earned ${coins} coins`, 'interstitial-ad');
        setCoinsEarned(coins);
        setShowSuccess(true);
      } else {
        setErrorMessage('Ad was not shown. Please try again.');
        setShowError(true);
      }
    } catch (error) {
      console.error('Error showing interstitial ad:', error);
      setErrorMessage('Failed to show ad. Please try again.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <AppHeader title="Watch Ads" showBackButton={false} />
      <IonContent fullscreen className="ion-padding">
        <div className="watch-ad-container">
          <IonCard>
            <IonCardContent>
              <div className="ad-header">
                <IonIcon icon={tvOutline} className="ad-icon" />
                <h2>Earn Coins by Watching Ads</h2>
                <p className="ad-description">
                  {isNative
                    ? 'Watch ads to earn coins! Complete the ad to receive your reward.'
                    : 'Rewarded ads are only available in the mobile app. Download the app to watch ads and earn coins!'}
                </p>
              </div>
            </IonCardContent>
          </IonCard>

          {isNative ? (
            <>
              {/* Rewarded Ad Card */}
              <IonCard className="ad-card">
                <IonCardContent>
                  <div className="ad-card-content">
                    <div className="ad-info">
                      <h3>🎯 Rewarded Ad</h3>
                      <p className="ad-reward">Earn 50 coins</p>
                      <p className="ad-description-small">
                        Watch a short video ad to earn coins. You must watch the entire ad to receive the reward.
                      </p>
                    </div>
                    <IonButton
                      expand="block"
                      color="primary"
                      size="large"
                      onClick={handleWatchRewardedAd}
                      disabled={loading || !adService.isAvailable()}
                      className="watch-ad-button"
                    >
                      {loading ? (
                        <>
                          <IonSpinner name="crescent" />
                          <span style={{ marginLeft: '10px' }}>Loading Ad...</span>
                        </>
                      ) : (
                        <>
                          <IonIcon icon={tvOutline} slot="start" />
                          Watch Ad (50 coins)
                        </>
                      )}
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Interstitial Ad Card */}
              <IonCard className="ad-card">
                <IonCardContent>
                  <div className="ad-card-content">
                    <div className="ad-info">
                      <h3>📱 Interstitial Ad</h3>
                      <p className="ad-reward">Earn 25 coins</p>
                      <p className="ad-description-small">
                        Watch a quick interstitial ad to earn coins instantly.
                      </p>
                    </div>
                    <IonButton
                      expand="block"
                      color="secondary"
                      size="large"
                      onClick={handleWatchInterstitialAd}
                      disabled={loading || !adService.isAvailable()}
                      className="watch-ad-button"
                    >
                      {loading ? (
                        <>
                          <IonSpinner name="crescent" />
                          <span style={{ marginLeft: '10px' }}>Loading Ad...</span>
                        </>
                      ) : (
                        <>
                          <IonIcon icon={tvOutline} slot="start" />
                          Watch Ad (25 coins)
                        </>
                      )}
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Info Card */}
              <IonCard>
                <IonCardContent>
                  <h3>💡 Tips</h3>
                  <ul className="ad-tips">
                    <li>Complete the entire ad to receive your coins</li>
                    <li>Rewarded ads give more coins but require watching the full video</li>
                    <li>Interstitial ads are shorter and award coins instantly</li>
                    <li>Ads may take a moment to load - please be patient</li>
                  </ul>
                </IonCardContent>
              </IonCard>
            </>
          ) : (
            <IonCard>
              <IonCardContent>
                <div className="web-notice">
                  <h3>📱 Mobile App Required</h3>
                  <p>
                    Rewarded ads are only available in the mobile app version. 
                    Please download the app on your iOS or Android device to watch ads and earn coins.
                  </p>
                  <p className="web-notice-small">
                    The web version does not support AdMob ads. For web monetization, 
                    we recommend using Google AdSense (which will be implemented separately).
                  </p>
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* Success Alert */}
          <IonAlert
            isOpen={showSuccess}
            onDidDismiss={() => setShowSuccess(false)}
            header="🎉 Success!"
            message={`You earned ${coinsEarned} coins for watching the ad!`}
            buttons={[
              {
                text: 'Watch Another',
                handler: () => {
                  setShowSuccess(false);
                },
              },
              {
                text: 'Close',
                role: 'cancel',
              },
            ]}
          />

          {/* Error Alert */}
          <IonAlert
            isOpen={showError}
            onDidDismiss={() => setShowError(false)}
            header="⚠️ Error"
            message={errorMessage}
            buttons={['OK']}
          />
        </div>
      </IonContent>
      <BottomNav />
    </IonPage>
  );
};

export default WatchAd;

